import { Button, Typography } from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { VerticalDivider } from '@hero/atoms';
import { OrderStatusBadge, Table } from '@hero/molecules';
import { TableProducts } from '@hero/organisms';
import { ROUTE_PATHS } from '@hero/routes';
import { useGetContact } from '@hero/services/contact-service/hooks';
import { useGetOrders } from '@hero/services/order-service/hook';

import * as S from './style';

const NameCell = ({ row }) => {
  const navigate = useNavigate();
  return (
    <Button
      variant="link"
      color="neutral"
      onClick={() => navigate(ROUTE_PATHS.MY_AUTOMATION_DETAIL(row.original.automationCode))}
    >
      {row.original.automation.name}
    </Button>
  );
};

const RecipientDetailsCell = ({ row }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  return (
    <Button
      variant="link"
      color="neutral"
      onClick={() =>
        navigate(
          ROUTE_PATHS.AUTOMATION_RECIPIENT_DETAIL(row.original.automationCode, row.original.code)
        )
      }
    >
      {t('seeRecipientGiftStatus', { recipientName: row.original.recipient.firstName })}
    </Button>
  );
};

const StatusCell = ({ row }) => <OrderStatusBadge status={row.original.giftStatus} />;

function ContactDetailAutomations() {
  const { t } = useTranslation();
  const { id } = useParams();

  const { data: contactData } = useGetContact({ id });

  const [globalFilters, setGlobalFilters] = useState({});
  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 5
  });

  const { data: automationRecipients, isLoading: isAutomationRecipientsLoading } = useGetOrders(
    {
      filters: {
        type: 'automation',
        email: contactData?.email
      },
      options: {
        populate: true
      },
      pagination: {
        page: pageIndex + 1,
        limit: pageSize
      }
    },
    { enabled: !!contactData?.id }
  );

  const renderSku = ({ getValue }) => {
    const value = getValue();

    if (!value.sku) {
      return <S.EmptyLine />;
    }

    return <TableProducts products={[value]} />;
  };

  const columns = [
    {
      header: t('name'),
      cell: NameCell
    },
    {
      header: t('recipientDetails'),
      cell: RecipientDetailsCell
    },
    { header: t('gift'), accessorKey: 'selectedSku', cell: renderSku },
    {
      header: t('status'),
      cell: StatusCell
    }
  ];

  return (
    <S.Automations>
      <S.ContactDetailHeader>
        <Typography variant="body2Bold">
          {t('activeAutomationIsPartOf', {
            firstName: contactData?.firstName
          })}
        </Typography>
        <VerticalDivider $margin="0 8px" $height="20px" />
        <Typography variant="body2">
          <Trans
            i18nKey="total"
            values={{
              count: automationRecipients?.totalDocs
            }}
            components={{
              b: <b />
            }}
          />
        </Typography>
      </S.ContactDetailHeader>
      <Table
        columns={columns || []}
        data={automationRecipients?.docs || []}
        loading={isAutomationRecipientsLoading}
        globalFilters={globalFilters}
        setGlobalFilters={setGlobalFilters}
        pageCount={automationRecipients?.totalPages}
        pagination={{
          pageIndex,
          pageSize,
          total: automationRecipients?.totalDocs
        }}
        paginationProps={{
          perPageSelection: true
        }}
        setPagination={setPagination}
        manualPagination
        enabled={{ filter: false, search: false, header: true }}
      />
    </S.Automations>
  );
}

NameCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      automation: PropTypes.shape({
        name: PropTypes.string
      }),
      automationCode: PropTypes.string
    })
  })
};

RecipientDetailsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      automationCode: PropTypes.string,
      code: PropTypes.string,
      recipient: PropTypes.shape({
        firstName: PropTypes.string
      })
    })
  })
};

StatusCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      giftStatus: PropTypes.string
    })
  })
};

export { ContactDetailAutomations };
